import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import classNames from "classnames";
import {  useParams } from "react-router-dom";
import { Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalBody, Button } from "reactstrap";
import { useGetInventoryDetail, useDeleteInventory, useUpdateInventory } from "../../api/inventory/service/index";
import PricingTab from './tabs/PricingTab';
import VehicleTab from './tabs/VehicleTab';
import OptionsTab from './tabs/OptionsTab';
import ImagesTab from './tabs/ImagesTab';
import FilesTab from './tabs/FilesTab';
import PurchaseTab from './tabs/PurchaseTab';
import ExpensesTab from './tabs/ExpensesTab';
import GLTab from './tabs/GLTab';
import TitlingTab from './tabs/TitlingTab';
import NotesTab from './tabs/NotesTab';
import FlagTab from './tabs/FlagTab';
import UserDefinedTab from './tabs/UserDefinedTab';
import InventoryInfoCards from './components/InventoryInfoCards';
import InventoryInfoCardsTheme2 from './components/InventoryInfoCardsTheme2';
import InventoryPrintDrawer from './components/InventoryPrintDrawer';
import { useGetAccount } from "api/account/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useTranslation } from 'react-i18next';
import SubHeader from "../../components/Navbars/SubHeader";
import { useClearListStateOnLeave, LIST_STATE_FLOWS } from "utils/listStateSessionStorage";

const INVENTORY_LIST_STATE_KEY = "dealertiq-inventory-list-state";

const SearchDetail = ({ activeColor = "blue", inventoryId, isEmbedded = false, defaultTab, shellThemeVariant = "1", isLightTheme = false }) => {
  const { t } = useTranslation();
  useClearListStateOnLeave(INVENTORY_LIST_STATE_KEY, LIST_STATE_FLOWS.inventory, {
    enabled: !isEmbedded,
  });
  
  const { id: urlId } = useParams();
  const id = inventoryId || urlId;
  const [activeTab, setActiveTab] = useState(defaultTab || "PRICING");

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const deleteInventoryMutation = useDeleteInventory();
  const [tabLoading, setTabLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { data: account } = useGetAccount();
  const updateMutation = useUpdateInventory();

  // Get active color style from utility function
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const handleImagesCountClick = useCallback(() => {
    setActiveTab("IMAGES");
  }, []);

  const [isSaving, setIsSaving] = useState(false);


  // Tab refs
  const pricingTabRef = useRef();
  const vehicleTabRef = useRef();
  const purchaseTabRef = useRef();
  const titlingTabRef = useRef();
  const userDefinedTabRef = useRef();
  const inventoryPrintDrawerRef = useRef(null);

  const [inventoryMainData, setInventoryMainData] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const tabScrollRef = useRef(null);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const isTheme2 = shellThemeVariant === "2";
  const useHorizontalMobileTabs = isMobile;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // API'den veri çek - React Query cache'ini kullan
  const { data, isLoading, isFetching } = useGetInventoryDetail(id, activeTab);

  const isSuperAdmin = !!account?.is_super_admin;

  // Super admin can edit everything regardless of tenant role permissions.
  const permissions = useMemo(() => {
    const base = account?.permissions || {};
    if (!isSuperAdmin) return base;
    return {
      ...base,
      inventory_update: true,
      inventory_delete: true,
      inventory_images_add: true,
      inventory_images_delete: true,
      inventory_files_add: true,
      inventory_expenses_add: true,
      inventory_expenses_delete: true,
      inventory_notes_add: true,
      inventory_ecabinet_add: true,
      inventory_flags_add: true,
      can_edit_stock_number: true,
    };
  }, [account?.permissions, isSuperAdmin]);

  useEffect(() => {
    const handleRemoteDelete = () => {
      if (permissions?.inventory_delete !== true) return;
      setDeleteModalOpen(true);
    };
    window.addEventListener('delete-inventory-requested', handleRemoteDelete);
    return () => window.removeEventListener('delete-inventory-requested', handleRemoteDelete);
  }, [permissions?.inventory_delete]);

  useEffect(() => {
    if (isEmbedded) return undefined;
    const handleRemotePrint = () => {
      if (
        inventoryPrintDrawerRef.current &&
        typeof inventoryPrintDrawerRef.current.open === "function"
      ) {
        inventoryPrintDrawerRef.current.open();
      }
    };
    window.addEventListener('print-inventory-requested', handleRemotePrint);
    return () => window.removeEventListener('print-inventory-requested', handleRemotePrint);
  }, [isEmbedded]);

  useEffect(() => {
    if (data && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [data, initialDataLoaded]);

  useEffect(() => {
    if (data) {
      if (!inventoryMainData) {
        setInventoryMainData(data);
      } else {
        setInventoryMainData(prev => ({ ...prev, ...data }));
      }
    }
  }, [data]);

  // Tab değiştiğinde sadece gerçekten yeni veri yüklenirken loading göster
  useEffect(() => {
    if (initialDataLoaded && isFetching && !isLoading) {
      setTabLoading(true);
      const timer = setTimeout(() => {
        setTabLoading(false);
      }, 200); // Daha kısa loading efekti
      return () => clearTimeout(timer);
    } else if (!isFetching) {
      setTabLoading(false);
    }
  }, [isFetching, isLoading, initialDataLoaded]);

  const tabs = [
    "PRICING", "VEHICLE", "OPTIONS", "IMAGES", "FILES", "PURCHASE", "EXPENSES", "GL", "TITLING", "NOTES", "ECABINET", "USER DEFINED", "FLAGS"
  ];

  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) {
      setCanScrollTabsRight(false);
      return;
    }
    setCanScrollTabsRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    if (!useHorizontalMobileTabs) {
      setCanScrollTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkTabScroll);
    };
    runCheck();
    const scrollEl = tabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [checkTabScroll, tabs.length, id, useHorizontalMobileTabs]);

  const handleTabScrollRight = useCallback(() => {
    if (tabScrollRef.current && canScrollTabsRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollTabsRight]);

  // Tab display names with translations
  const getTabDisplayName = (tab) => {
    const tabMap = {
      "PRICING": t('searchDetail.tabs.pricing'),
      "VEHICLE": t('searchDetail.tabs.vehicle'),
      "OPTIONS": t('searchDetail.tabs.options'),
      "IMAGES": t('searchDetail.tabs.images'),
      "FILES": t('searchDetail.tabs.files'),
      "PURCHASE": t('searchDetail.tabs.purchase'),
      "EXPENSES": t('searchDetail.tabs.expenses'),
      "GL": t('searchDetail.tabs.gl'),
      "TITLING": t('searchDetail.tabs.titling'),
      "NOTES": t('searchDetail.tabs.notes'),
      "ECABINET": t('searchDetail.tabs.ecabinet'),
      "USER DEFINED": t('searchDetail.tabs.userDefined'),
      "FLAGS": t('searchDetail.tabs.flags')
    };
    return tabMap[tab] || tab;
  };


  // Fiyatı dolar formatında gösteren yardımcı fonksiyon
  const formatUSD = useCallback((value) => {
    if (value === 0 || value === '0') return '$0';
    if (!value) return '';
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.\-]/g, '')) : value;
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }, []);

  // Araç yaşı ve stok günü — state + effect yerine türetilmiş değer (yaş 0 iken !vehicleAge sonsuz döngüsü oluşuyordu).
  const vehicleAge = useMemo(() => {
    if (!data?.inventory_vehicle?.year) return undefined;
    const y = parseInt(data.inventory_vehicle.year, 10);
    if (!Number.isFinite(y)) return undefined;
    const age = new Date().getFullYear() - y;
    return Number.isFinite(age) ? age : undefined;
  }, [id, data?.inventory_vehicle?.year]);

  const daysInInventory = useMemo(() => {
    if (!data?.createdAt) return "-";
    const d = Math.floor((new Date() - new Date(data.createdAt)) / (1000 * 60 * 60 * 24));
    return Number.isFinite(d) ? d : "-";
  }, [id, data?.createdAt]);

  const handleConfirmDelete = async () => {
    try {
      await deleteInventoryMutation.mutateAsync({
        id: id,
      });
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting inventory:", error);
      // Error handling is now done in the mutation hook
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  // Save fonksiyonu
  const handleSave = async () => {
    setIsSaving(true);
    try {
      let result = { success: false };

      // Aktif tab'a göre save işlemi yap
      if (activeTab === "PRICING" && pricingTabRef.current) {
        result = await pricingTabRef.current.handleSave();
      }
      if (activeTab === "VEHICLE" && vehicleTabRef.current) {
        result = await vehicleTabRef.current.handleSave();
      }
      if (activeTab === "TITLING" && titlingTabRef.current) {
        result = await titlingTabRef.current.handleSave();
      }
      if (activeTab === "USER DEFINED" && userDefinedTabRef.current) {
        result = await userDefinedTabRef.current.handleSave();
      }
      if (activeTab === "PURCHASE" && purchaseTabRef.current) {
        result = await purchaseTabRef.current.handleSave();
      }
      // Diğer tab'lar için de benzer işlemler eklenebilir

    } catch (error) {
      console.log("SearchDetail - error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // İlk yükleme sırasında loading göster
  if (isLoading && !initialDataLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="spinner-border" role="status" style={{ color: activeColorStyle.borderColor }}>
          <span className="sr-only">Loading...</span>
        </div>
        <span className="ml-2" style={{ color: activeColorStyle.borderColor }}>{t('searchDetail.loadingInventoryData')}</span>
      </div>
    );
  }



  // Sold/deleted inventories are locked for normal users; super admin can still edit.
  const isSold = (data?.inventory_vehicle?.vehicle_status?.status_key === 'sold' || data?.inventory_vehicle?.vehicle_status?.status_key === 'deleted');
  const isInventoryLocked = isSold && !isSuperAdmin;
  const canUpdateInventory = !!permissions?.inventory_update;

  const contentComponent = (
    <>
      {/* Tab Menüsü ve İçerik - Ayrı Bölüm */}
      <div
        className={
          useHorizontalMobileTabs
            ? "w-100 search-detail-tabs detail-mobile-tabs"
            : "w-100 search-detail-tabs"
        }
      >
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
          {useHorizontalMobileTabs ? (
            <div className="d-flex align-items-center flex-grow-1 min-width-0 w-100">
              <div
                ref={tabScrollRef}
                className="flex-grow-1 channel-tabs-scroll"
                style={{ minWidth: 0 }}
              >
                <Nav
                  tabs
                  className="border-0"
                  style={{
                    borderBottom: "none",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    minWidth: "max-content",
                    width: "auto",
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {tabs.map((tab) => (
                    <NavItem key={tab} style={{ flex: "0 0 auto" }}>
                      <NavLink
                        href="#"
                        className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                        style={{
                          background: "none",
                          border: "none",
                          position: "relative",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                          fontWeight: activeTab === tab ? 700 : 500,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab(tab);
                        }}
                      >
                        {getTabDisplayName(tab)}
                        {activeTab === tab && (
                          <div
                            style={{
                              height: 3,
                              background: activeColorStyle.borderColor,
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: -8,
                            }}
                          />
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>
              {canScrollTabsRight && (
                <button
                  type="button"
                  className="btn btn-link p-0 flex-shrink-0"
                  onClick={handleTabScrollRight}
                  style={{
                    color: activeColorStyle.borderColor,
                    lineHeight: 1,
                  }}
                  aria-label={t("common.scrollTabs")}
                >
                  <i className="tim-icons icon-minimal-right" style={{ fontSize: 20 }} />
                </button>
              )}
            </div>
          ) : (
            <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
            {tabs.map(tab => (
              <NavItem key={tab}>
                <NavLink
                  href="#"
                  className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                  style={{
                    background: "none",
                    border: "none",
                    position: "relative",
                    cursor: 'pointer',
                    color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                    fontWeight: activeTab === tab ? 700 : 500

                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(tab);
                  }}
                >
                  {getTabDisplayName(tab)}
                  {activeTab === tab && (
                    <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 0, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                  )}
                </NavLink>
              </NavItem>
            ))}
            </Nav>
          )}

          {/* Desktop View Save Button */}
          {canUpdateInventory && (activeTab === "PRICING" || activeTab === "VEHICLE" || activeTab === "TITLING" || activeTab === "USER DEFINED" || activeTab === "PURCHASE") && (
            <div className="d-none d-lg-flex align-items-center">
              <button
                className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ ...activeColorStyle, color: 'white', cursor: 'pointer', margin: 0 }}
                onClick={handleSave}
                disabled={isSaving || isInventoryLocked}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                    {t('searchDetail.saving')}
                  </>
                ) : (
                  t('searchDetail.save')
                )}
              </button>
            </div>
          )}

          {/* Mobile View Save Button */}
          {canUpdateInventory && (activeTab === "PRICING" || activeTab === "VEHICLE" || activeTab === "TITLING" || activeTab === "USER DEFINED" || activeTab === "PURCHASE") && (
            <div className="d-flex d-lg-none flex-column w-100 mt-2">
              <button
                className="btn btn-sm w-100 d-inline-flex align-items-center justify-content-center"
                style={{ ...activeColorStyle, color: 'white', cursor: 'pointer', padding: '12px', margin: 0 }}
                onClick={handleSave}
                disabled={isSaving || isInventoryLocked}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                    {t('searchDetail.saving')}
                  </>
                ) : (
                  t('searchDetail.save')
                )}
              </button>
            </div>
          )}
        </div>
        <TabContent activeTab={activeTab} >
          <TabPane tabId={activeTab}>
            {tabLoading && isFetching ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100px" }}>
                <div className="spinner-border spinner-border-sm" role="status" style={{ color: activeColorStyle.borderColor }}>
                  <span className="sr-only">Loading...</span>
                </div>
                <span className="ml-2" style={{ color: activeColorStyle.borderColor, fontSize: '14px' }}>{t('searchDetail.updating')}</span>
              </div>
            ) : (
              <>
                {activeTab === "PRICING" && (
                  <PricingTab
                    isLoading={isLoading}
                    ref={pricingTabRef}
                    inventoryId={id}
                    inventoryData={data}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "VEHICLE" && (
                  <VehicleTab
                    ref={vehicleTabRef}
                    inventoryId={id}
                    activeColor={activeColor}
                    permission={permissions}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "OPTIONS" && (
                  <OptionsTab
                    inventoryId={id}
                    isDisabled={isInventoryLocked}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "IMAGES" &&
                  <ImagesTab
                    permission={{
                      ...permissions,
                      inventory_images_add: permissions?.inventory_images_add,
                      inventory_images_delete: permissions?.inventory_images_delete
                    }}
                    id={data?.id}
                    inventoryData={data?.images}
                    module="inventory"
                    isLoading={isLoading}
                    activeColor={activeColor} />}
                {activeTab === "FILES" &&
                  <FilesTab
                    module="inventory"
                    permission={
                      {
                        add_files: permissions?.inventory_files_add,
                        delete_files: permissions?.inventory_files_add
                      }
                    }
                    id={data?.id}
                    type="file"
                    data={data?.files}
                    isLoading={isLoading}
                    activeColor={activeColor} />}
                {activeTab === "PURCHASE" && (
                  <PurchaseTab
                    ref={purchaseTabRef}
                    permission={permissions}
                    inventoryId={id}
                    inventoryData={data}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "EXPENSES" && (
                  <ExpensesTab
                    permission={{
                      ...permissions,
                      inventory_expenses_add: permissions?.inventory_expenses_add,
                      inventory_expenses_delete: permissions?.inventory_expenses_delete,
                    }}
                    inventoryId={id}
                    inventoryData={data}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "GL" && (
                  <GLTab inventoryData={data?.gl} activeColor={activeColor} isTheme2={isTheme2} />
                )}
                {activeTab === "TITLING" && (
                  <TitlingTab
                    permission={permissions}
                    inventoryId={id}
                    ref={titlingTabRef}
                    inventoryData={data?.titling}
                    activeColor={activeColor}
                    isLoading={isLoading}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "NOTES" && (
                  <NotesTab
                    module="inventory"
                    id={inventoryMainData?.id}
                    permission={{
                      notes_add: permissions?.inventory_notes_add
                    }}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "ECABINET" &&
                  <FilesTab
                    module="inventory"
                    type="ecabinet"
                    permission={{
                      add_files: permissions?.inventory_ecabinet_add,
                      delete_files: permissions?.inventory_ecabinet_add
                    }}
                    id={data?.id}
                    data={data?.ecabinets}
                    isLoading={isLoading}
                    activeColor={activeColor}
                  />}
                {activeTab === "USER DEFINED" && <UserDefinedTab permission={permissions} data={data?.user_defineds} activeColor={activeColor} id={id} module="inventory" ref={userDefinedTabRef} />}
                {activeTab === "FLAGS" && (
                  <FlagTab
                    update={updateMutation}
                    module="inventory"
                    permission={permissions}
                    flags={data?.flags}
                    id={id}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
              </>
            )}
          </TabPane>
        </TabContent>
      </div>
    </>
  );

  // If embedded, return just the content without the full page layout
  if (isEmbedded) {
    return contentComponent;
  }

  // Full page layout for standalone usage
  return (
    <div className="content" style={{ minHeight: "80vh", paddingTop: isMobile ? "7rem" : null, position: "relative" }}>
      <div style={{ width: '100%', position: "relative", zIndex: 1, }}>
        <SubHeader
          activeColor={activeColor}
          title={"Inventory " + (inventoryMainData?.inventory_vehicle?.vin || id)}
          back={-1}
          backFallback="/inventory"
          status={inventoryMainData?.inventory_vehicle?.vehicle_status}
          theme2ScrollGapFill={shellThemeVariant === "2"}
          isLightTheme={isLightTheme}
        />
        {/* Üstteki Dört Kutu - Memoized Component */}
        <div
          className={classNames("search-detail-info-cards", {
            "search-detail-info-cards--theme2": isTheme2,
          })}
        >
          {isTheme2 ? (
            <InventoryInfoCardsTheme2
              data={inventoryMainData}
              formatUSD={formatUSD}
              vehicleAge={vehicleAge}
              daysInInventory={daysInInventory}
              isLightTheme={isLightTheme}
              onImagesCountClick={handleImagesCountClick}
              imagesCountLinkColor={activeColorStyle.borderColor}
            />
          ) : (
            <InventoryInfoCards
              data={inventoryMainData}
              activeColor={activeColor}
              getActiveColorStyle={() => activeColorStyle}
              formatUSD={formatUSD}
              vehicleAge={vehicleAge}
              daysInInventory={daysInInventory}
              onImagesCountClick={handleImagesCountClick}
            />
          )}
        </div>

        {contentComponent}

        {/* Portal target in Footer.js will show the Remove Button */}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            style={{ cursor: 'pointer' }}
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('searchDetail.deleteInventory')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('searchDetail.confirmDelete')}</p>
          <p className="text-muted">{t('searchDetail.cannotUndone')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            style={{ cursor: 'pointer' }}
            onClick={handleCancelDelete}
            disabled={deleteInventoryMutation.isPending}
          >
            {t('searchDetail.cancel')}
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white',
              cursor: 'pointer'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteInventoryMutation.isPending}
          >
            {deleteInventoryMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {t('searchDetail.deleting')}
              </>
            ) : (
              t('searchDetail.yesDelete')
            )}
          </Button>
        </div>
      </Modal>

      {!isEmbedded && (
        <InventoryPrintDrawer
          ref={inventoryPrintDrawerRef}
          inventoryId={id}
          activeColorStyle={activeColorStyle}
          isLightTheme={isLightTheme}
        />
      )}

    </div>
  );
};

export default SearchDetail; 